import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import type { ApiResponse } from '@owl-nest/api-client'
import * as hooks from '@owl-nest/hooks'

export async function get(id: number): Promise<api.HttpResult<api.RewardShipping>> {
  const response = await api.client('GET /rewards/:id/shippings', {
    urlParams: { id: String(id) },
  })

  return response.next((success) => {
    return success.body
  })
}

export async function upsertRewardShipping(
  id: number,
  payload: api.CreateRewardShipping,
): Promise<api.HttpResult<api.RewardShipping>> {
  const response = await api.client('PATCH /rewards/:id/shippings', {
    urlParams: { id: String(id) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function deleteRewardShipping(id: number): Promise<ApiResponse<void>> {
  const response = await api.client('DELETE /rewards/:id/shippings', {
    urlParams: { id: String(id) },
  })

  return response.next((success) => success.body)
}

const REWARD_SHIPPING_ATOM = jotai.atom<hooks.Cache<api.RewardShipping>>({})

export type UseRewardShipping = {
  response: hooks.Response<api.RewardShipping>
  query: () => void
  update: (payload: api.CreateRewardShipping) => Promise<ApiResponse<api.RewardShipping> | undefined>
  create: (id: number, payload: api.CreateRewardShipping) => Promise<ApiResponse<api.RewardShipping> | undefined>
  remove: () => Promise<ApiResponse<unknown> | undefined>
}

export function useRewardShipping(
  rewardId: number | undefined,
  config: {
    refreshProject: () => void
  },
): UseRewardShipping {
  const sharedQuery = hooks.useSharedQuery(
    async (rewardId: number) => {
      return get(rewardId)
    },
    {
      atom: REWARD_SHIPPING_ATOM,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount: true, query, getResponse }, id(rewardId))

  return {
    query,
    update,
    create,
    remove,
    response: getResponse(),
  }

  function query(): Promise<void> | undefined {
    if (rewardId) {
      return sharedQuery.query(rewardId)
    }
  }

  async function update(payload: api.CreateRewardShipping): Promise<ApiResponse<api.RewardShipping> | undefined> {
    if (!rewardId) {
      return
    }

    return sharedQuery.patch([rewardId], async () => {
      const responseUpsert = await upsertRewardShipping(rewardId, payload)
      responseUpsert.next(() => {
        config.refreshProject()
      })

      return responseUpsert
    })
  }

  async function create(
    customId: number,
    payload: api.CreateRewardShipping,
  ): Promise<ApiResponse<api.RewardShipping> | undefined> {
    const responseUpsert = sharedQuery.patch([customId], async () => await upsertRewardShipping(customId, payload))
    responseUpsert.then((response) => {
      if (response?.isRight()) {
        config.refreshProject()
      }
    })

    return responseUpsert
  }

  async function remove(): Promise<ApiResponse<unknown> | undefined> {
    if (!rewardId) {
      return
    }

    const responseDelete = sharedQuery.remove([rewardId], async () => await deleteRewardShipping(rewardId))
    responseDelete.then((response) => {
      if (response?.isRight()) {
        config.refreshProject()
      }
    })

    return responseDelete
  }

  function id(rewardId?: number): string {
    return JSON.stringify([rewardId])
  }

  function getResponse(): hooks.Response<api.RewardShipping> {
    if (rewardId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = sharedQuery.getCurrentResponse([rewardId])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }
}
